import { type MouseEvent, type ReactNode, useState } from "react";
import type { FaqItem } from "src/api/faq";
import Image from "src/components/Default/Image";
import { markdownToHtml } from "src/helpers/markdownToHtml";

interface GuideBlockProps {
	item: FaqItem;
	// service chip (and staff actions), shown only on the first block
	header?: ReactNode;
}

export const GuideBlock = ({ item, header }: GuideBlockProps) => {
	const [lightbox, setLightbox] = useState<{
		images: { image: string }[];
		index: number;
	} | null>(null);

	// ponytail: delegated click instead of per-img/per-a listeners (NotePreview pattern) - content is static html
	const handleClick = (e: MouseEvent<HTMLDivElement>) => {
		const target = e.target as HTMLElement;
		const link = target.closest("a");
		if (link?.href) {
			e.preventDefault();
			window.open(link.href, "_blank", "noopener");
			return;
		}
		if (target.tagName !== "IMG") return;
		const imgs = Array.from(e.currentTarget.querySelectorAll("img"));
		setLightbox({
			images: imgs.map((i) => ({ image: i.src })),
			index: imgs.indexOf(target as HTMLImageElement),
		});
	};

	return (
		<div
			className="basic-box flex flex-col gap-32 w-100"
			data-test-id="guide-block"
		>
			{header}
			<div
				className="rich-text rich-text--zoomable"
				onClick={handleClick}
				// ponytail: staff html goes through the markdown pipeline only for its sanitizer; swap for a plain html sanitizer if markdown parsing ever mangles content
				dangerouslySetInnerHTML={{
					__html: markdownToHtml(item.content, true),
				}}
			/>
			{lightbox && (
				<Image
					images={lightbox.images}
					index={lightbox.index}
					close={() => setLightbox(null)}
				/>
			)}
		</div>
	);
};
